import { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { Building2, ShieldAlert, ShieldCheck, Shield, ShieldX, CheckCircle2, Clock, AlertTriangle, FileQuestion } from 'lucide-react';
import OTDDrilldownModal from '../../components/drilldown/OTDDrilldownModal';
import { SupplierWithStatus, SupplierStatus } from './types';
import { STATUS_CONFIG } from './constants';
import { getRiskLevelStyle } from './utils';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';

type QuestionnaireOverall = 'up_to_date' | 'expiring' | 'overdue' | 'pending' | 'none';

const QUESTIONNAIRE_BADGE_CONFIG: Record<QuestionnaireOverall, { label: string; className: string; Icon: typeof CheckCircle2 }> = {
  up_to_date: {
    label: 'Up to Date',
    className: 'bg-green-50 text-green-700 border-green-200',
    Icon: CheckCircle2,
  },
  expiring: {
    label: 'Expiring Soon',
    className: 'bg-amber-50 text-amber-700 border-amber-200',
    Icon: Clock,
  },
  overdue: {
    label: 'Overdue',
    className: 'bg-red-50 text-red-700 border-red-200',
    Icon: AlertTriangle,
  },
  pending: {
    label: 'Pending',
    className: 'bg-gray-50 text-gray-700 border-gray-200',
    Icon: FileQuestion,
  },
  none: {
    label: 'Not Assigned',
    className: 'bg-gray-50 text-gray-500 border-gray-200',
    Icon: FileQuestion,
  },
};

interface SupplierCardProps {
  supplier: SupplierWithStatus;
  onNavigate: (path: string) => void;
}

export function SupplierCard({ supplier, onNavigate }: SupplierCardProps) {
  const { isSupplierCardFieldEnabled } = useMenuSettings();
  const status = supplier.onboarding_status_derived || 'draft';
  // CR-5557: clicking the OTD tile opens the drill-through directly (does
  // not navigate to the detail page) so users can validate the rate from
  // the supplier list without an extra hop.
  const [otdDrilldownOpen, setOtdDrilldownOpen] = useState(false);

  const showLogo = isSupplierCardFieldEnabled('logo');
  const showRisk = isSupplierCardFieldEnabled('risk_badge');
  const showOtd = isSupplierCardFieldEnabled('otd');
  const showRating = isSupplierCardFieldEnabled('rating');
  const showQuestionnaireStatus = isSupplierCardFieldEnabled('questionnaire_status');

  const getActionButton = () => {
    switch (status) {
      case 'approved':
      case 'due_diligence':
        // Approved suppliers now surface questionnaire state via the badge below,
        // not via a button. Workflow buttons only appear for non-approved states.
        return null;
      case 'not_approved':
        return (
          <Button
            size="default"
            variant="outline"
            className="text-sm font-medium bg-red-50 text-red-700 border-red-200 hover:bg-red-100 px-4"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(`/suppliers/wizard/${supplier.company_id}`);
            }}
          >
            Re-open Approval Request
          </Button>
        );
      case 'pending_initial':
        return (
          <Button
            size="default"
            variant="outline"
            className="text-sm font-medium bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100 px-4"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(`/onboarding?review=${supplier.id}`);
            }}
          >
            Review Approval
          </Button>
        );
      case 'invited':
        return (
          <Button
            size="default"
            variant="outline"
            className="text-sm font-medium bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100 px-4"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(`/suppliers/wizard/${supplier.company_id}`);
            }}
          >
            View Approval Request
          </Button>
        );
      case 'draft':
        return (
          <Button
            size="default"
            variant="outline"
            className="text-sm font-medium bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 px-4"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(`/suppliers/wizard/${supplier.company_id}`);
            }}
          >
            Continue Setup
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <Card
      className="hover:shadow-lg transition-shadow cursor-pointer"
      onClick={() => {
        // CR-4579: Supplier groups navigate to group detail page
        if ((supplier as unknown as Record<string, unknown>)['is_supplier_group']) {
          const groupId = (supplier as unknown as Record<string, unknown>)['group_id'];
          if (groupId) onNavigate(`/suppliers/groups/${groupId}`);
          return;
        }
        if (supplier.company_id) onNavigate(`/suppliers/${supplier.company_id}`);
      }}
    >
      <CardContent className="p-4 text-center space-y-3">
        {/* Company Logo/Icon */}
        {showLogo && (
          <div className="flex justify-center">
            <div className="w-full h-32 rounded-md flex items-center justify-center bg-gray-50/50 p-2">
              {(supplier.company_details as unknown as Record<string, unknown>)?.['logo_url'] ? (
                <img
                  src={(supplier.company_details as unknown as Record<string, unknown>)['logo_url'] as string}
                  alt={supplier.company_name}
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <Building2 className="h-14 w-14 text-gray-300" />
              )}
            </div>
          </div>
        )}

        {/* Company Name */}
        <div className="min-h-[28px] flex items-center justify-center">
          <h3 className="text-sm font-semibold line-clamp-2 leading-tight" title={supplier.company_name}>
            {supplier.company_name}
          </h3>
        </div>

        {/* CR-4497 + CR-4772: Risk Level + Credit Score badges */}
        {((showRisk && supplier.risk_level && supplier.risk_level !== 'none')
          || (typeof supplier.latest_credit_score === 'number')) && (
          <div className="flex items-center justify-center gap-1.5 flex-wrap">
            {showRisk && supplier.risk_level && supplier.risk_level !== 'none' && (
              <Badge className={`text-xs font-medium px-2.5 py-1 ${getRiskLevelStyle(supplier.risk_level).badgeColor}`}>
                {getRiskLevelStyle(supplier.risk_level).icon === 'critical' ? (
                  <ShieldX className="h-3.5 w-3.5 mr-1" />
                ) : getRiskLevelStyle(supplier.risk_level).icon === 'high' ? (
                  <ShieldAlert className="h-3.5 w-3.5 mr-1" />
                ) : getRiskLevelStyle(supplier.risk_level).icon === 'low' ? (
                  <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                ) : (
                  <Shield className="h-3.5 w-3.5 mr-1" />
                )}
                {getRiskLevelStyle(supplier.risk_level).label}
              </Badge>
            )}
            {typeof supplier.latest_credit_score === 'number' && (() => {
              // CR-4772: Credit score banding mirrors ComplianceTab's distribution cuts
              // (Excellent 800+, Good 700-799, Fair 600-699, Poor <600). Styling is
              // parallel to the risk badge — colour encodes the tier.
              const score = supplier.latest_credit_score;
              const band = score >= 800
                ? { label: 'Excellent', cls: 'bg-green-50 text-green-700 border-green-200' }
                : score >= 700
                  ? { label: 'Good', cls: 'bg-blue-50 text-blue-700 border-blue-200' }
                  : score >= 600
                    ? { label: 'Fair', cls: 'bg-amber-50 text-amber-700 border-amber-200' }
                    : { label: 'Poor', cls: 'bg-red-50 text-red-700 border-red-200' };
              return (
                <Badge className={`text-xs font-medium px-2.5 py-1 ${band.cls}`} title={`Credit Score ${score} — ${band.label}`}>
                  Credit {score}
                </Badge>
              );
            })()}
          </div>
        )}

        {/* CR-4475: Key metrics for approved suppliers */}
        {status === 'approved' && (showOtd || showRating) && (
          <div className={`grid ${showOtd && showRating ? 'grid-cols-2' : 'grid-cols-1'} gap-2 text-xs`}>
            {showOtd && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOtdDrilldownOpen(true);
                }}
                className="bg-muted/40 rounded px-2 py-1.5 text-left hover:bg-muted/70 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring"
                title="Click to view the GRNs included in this rate"
              >
                <span className="text-muted-foreground block text-xs">
                  OTD
                  {/* CR-5557: surface the timeframe so the user can validate
                      what window the rate is calculated over. */}
                  {supplier.otd_timeframe_label && (
                    <span className="ml-1 opacity-70">· {supplier.otd_timeframe_label}</span>
                  )}
                </span>
                <span className={`font-semibold text-base ${
                  supplier.on_time_delivery_rate >= 90 ? 'text-green-600' :
                  supplier.on_time_delivery_rate >= 70 ? 'text-amber-600' : 'text-red-600'
                }`}>
                  {supplier.on_time_delivery_rate?.toFixed(0) ?? '0'}%
                </span>
              </button>
            )}
            {showRating && (
              <div className="bg-muted/40 rounded px-2 py-1.5">
                <span className="text-muted-foreground block text-xs">Rating</span>
                <span className="font-semibold text-base">
                  {supplier.overall_rating ? Number(supplier.overall_rating).toFixed(1) : '-'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Questionnaire compliance badge (approved suppliers, only when at least one is assigned) */}
        {status === 'approved' && showQuestionnaireStatus && supplier.questionnaire_status && supplier.questionnaire_status.total > 0 && (() => {
          const qs = supplier.questionnaire_status;
          const cfg = QUESTIONNAIRE_BADGE_CONFIG[qs.overall];
          const fraction = qs.total > 0 ? `${qs.approved - qs.overdue}/${qs.total}` : null;
          const handleClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            if (supplier.company_id) onNavigate(`/suppliers/${supplier.company_id}?tab=questionnaires`);
          };
          return (
            <div className="flex items-center justify-center">
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={handleClick}
                      className="focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full"
                      aria-label={`${cfg.label} questionnaire status — click to view details`}
                    >
                      <Badge variant="outline" className={`text-xs font-medium px-2.5 py-1 gap-1 cursor-pointer hover:shadow-sm transition-shadow ${cfg.className}`}>
                        <cfg.Icon className="h-3.5 w-3.5" />
                        {cfg.label}
                        {fraction && <span className="ml-1 opacity-75">({fraction})</span>}
                      </Badge>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    {qs.items.length === 0 ? (
                      <div className="text-xs">No questionnaires assigned.</div>
                    ) : (
                      <div className="space-y-1">
                        <div className="text-xs font-semibold mb-1">Questionnaire Status</div>
                        {qs.items.map((item, idx) => {
                          const itemCfg = QUESTIONNAIRE_BADGE_CONFIG[item.status];
                          const dateLabel = item.submitted_at
                            ? new Date(item.submitted_at).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
                            : '—';
                          return (
                            <div key={idx} className="flex items-center gap-2 text-xs">
                              <itemCfg.Icon className="h-3 w-3 flex-shrink-0" />
                              <span className="flex-1 truncate">{item.name}</span>
                              <span className="opacity-75">{dateLabel}</span>
                            </div>
                          );
                        })}
                        <div className="text-[10px] opacity-60 pt-1 border-t border-border/40 mt-1">
                          Click to open Questionnaires tab
                        </div>
                      </div>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          );
        })()}

        {/* Action Button based on status (non-approved lifecycle only) */}
        {getActionButton() && (
          <div className="flex items-center justify-center">
            {getActionButton()}
          </div>
        )}

        {/* Progress Bar for Draft status */}
        {status === 'draft' && supplier.draft_progress !== undefined && (
          <div className="space-y-1">
            <Progress value={supplier.draft_progress} className="h-4" />
            <span className="text-xs text-muted-foreground">
              {supplier.draft_progress}% complete
            </span>
          </div>
        )}

        {/* Onboarding Status Badge */}
        {status !== 'approved' && (
          <Badge className={`text-xs font-medium px-2.5 py-1 ${STATUS_CONFIG[status as SupplierStatus]?.badgeColor || 'bg-gray-500 text-white'}`}>
            {STATUS_CONFIG[status as SupplierStatus]?.label || status}
          </Badge>
        )}
      </CardContent>
      {/* CR-5557: OTD tile drill-through (opened via the OTD button above) */}
      <OTDDrilldownModal
        isOpen={otdDrilldownOpen}
        onClose={() => setOtdDrilldownOpen(false)}
        contextLabel={supplier.company_name}
        filters={{ supplier: supplier.company_id }}
      />
    </Card>
  );
}
